/**
 * Display-status derivation for games.
 *
 * A game's persisted `GameStatus` stays SCHEDULED until both teams approve the
 * result (then COMPLETED). But once a result has been *entered* (and is awaiting
 * approval) we show it as "Played" so neither team sees a stale "Scheduled".
 * This is a purely presentational layer — it never changes the persisted status.
 */
import { GameStatus } from '@sulis-liiga/common';

export type DisplayStatusTone =
  | 'pending'
  | 'scheduled'
  | 'played'
  | 'completed'
  | 'disputed'
  | 'cancelled'
  | 'abandoned';

export interface DisplayStatus {
  label: string;
  tone: DisplayStatusTone;
}

/**
 * Derive the label + tone to show for a game.
 *
 * @param status               the persisted lifecycle status
 * @param hasUnverifiedResult  a result has been entered but not yet approved
 * @param cancellationReason   optional reason for a cancelled game ('abandoned' renders differently)
 */
export function deriveDisplayStatus(
  status: GameStatus,
  hasUnverifiedResult: boolean,
  cancellationReason?: 'cancelled' | 'abandoned',
): DisplayStatus {
  if (status === GameStatus.COMPLETED) return { label: 'Completed', tone: 'completed' };
  if (status === GameStatus.DISPUTED) return { label: 'Disputed', tone: 'disputed' };
  if (status === GameStatus.CANCELLED) {
    if (cancellationReason === 'abandoned') return { label: 'Abandoned', tone: 'abandoned' };
    return { label: 'Cancelled', tone: 'cancelled' };
  }
  if (hasUnverifiedResult) return { label: 'Played', tone: 'played' };
  // A fixture nobody has proposed yet — "Pending to Propose" (empty schedule).
  if (status === GameStatus.PENDING) return { label: 'Pending to Propose', tone: 'pending' };
  // A fixture with an open proposal under negotiation — "Pending Proposal". It
  // only becomes "Scheduled" once a slot is accepted.
  if (status === GameStatus.PROPOSED) return { label: 'Pending Proposal', tone: 'pending' };
  return { label: 'Scheduled', tone: 'scheduled' };
}

/** Tailwind badge classes for each display tone. */
export const STATUS_TONE_CLASS: Record<DisplayStatusTone, string> = {
  pending: 'bg-indigo-50 text-indigo-700',
  scheduled: 'bg-gray-100 text-gray-700',
  played: 'bg-amber-100 text-amber-800',
  completed: 'bg-success-100 text-success-800',
  disputed: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-gray-100 text-gray-500',
  abandoned: 'bg-amber-100 text-amber-800',
};
